import clsx from "classnames"
import { GridSlot, GridSlotSize } from "common/types/graphql"
import { PropsWithChildren, forwardRef, ForwardedRef, DragEvent } from "react"

export interface ImmutableGridSlotProps {
  /** True if the grid slot should be rendered as a placeholder, false otherwise. */
  asPlaceholder?: boolean

  /** indicates whether the grid slot is being dropped (mouse over) */
  isOver?: boolean

  /** The size of the grid slot. */
  size?: GridSlotSize

  /** The type of the grid slot. */
  type?: GridSlot

  /** Function to be called when after a drop event. */
  handleDrop?: (ev: DragEvent<HTMLDivElement>) => void

  /** Function to be called when a draggable element is over the top of this. */
  handleDragOver?: (ev: DragEvent<HTMLDivElement>) => void

  /** Function to be called when the thing in the grid has started to be dragged */
  handleDragStart?: (ev: DragEvent<HTMLDivElement>) => void
}

/** A read-only grid slot. */
export const ImmutableGridSlot = forwardRef(function ImmutableGridSlot(
  {
    size = GridSlotSize.Base,
    asPlaceholder = false,
    isOver = false,
    children,
    handleDrop,
    handleDragOver,
    handleDragStart,
  }: PropsWithChildren<ImmutableGridSlotProps>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      className={clsx("group relative col-span-full flex overflow-hidden", {
        "sm:col-span-3": size === GridSlotSize.Base,
        "sm:col-span-6": size === GridSlotSize.BaseWide,
        "row-span-2 sm:col-span-6": size === GridSlotSize.Lg,
        "row-span-4 sm:col-span-6": size === GridSlotSize.Xl,
        "row-span-2 sm:col-span-9": size === GridSlotSize.NearFullByTwo,
        "row-span-3 sm:col-span-9 2xl:col-span-7": size === GridSlotSize.NearFullByThree,
        "row-span-4 sm:col-span-9 2xl:col-span-7": size === GridSlotSize.NearFullByFour,
        "row-span-1 sm:col-span-3 2xl:col-span-5": size === GridSlotSize.DisappearingThumbnail,
        "m-2.5 min-h-28 bg-blue-100 outline-offset-8 outline-dashed": asPlaceholder,
        "outline-2 outline-offset-4 outline-gray-700 outline-dashed": isOver,
        "outline-1 outline-blue-500": asPlaceholder && !isOver,
        "outline-2 outline-gray-700": asPlaceholder && isOver,
        "border-t pt-4": !asPlaceholder,
      })}
      ref={ref}
      data-testid="ImmutableGridSlot"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
      draggable={asPlaceholder ? "false" : "true"}
    >
      {children}
    </div>
  )
})
